<template>
  <div class="subtitle-scenarios">
    <h2 class="section-title">{{ $t('subtitlePage.scenarios.title') }}</h2>

    <div class="scenarios-grid">
      <div
        v-for="scenario in scenarios"
        :key="scenario.name"
        class="scenario-card"
      >
        <div class="scenario-image">
          <img :src="scenario.image" :alt="scenario.title" loading="lazy" />
          <div class="image-overlay">
            <Icon :name="scenario.icon" size="48" />
          </div>
        </div>

        <div class="scenario-content">
          <h3 class="scenario-title">{{ scenario.title }}</h3>
          <p class="scenario-description">{{ scenario.description }}</p>

          <ul class="scenario-examples">
            <li v-for="(example, index) in scenario.examples" :key="index">
              <Icon name="material-symbols:check-circle" size="16" />
              <span>{{ example }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
const scenarios = [
  {
    name: 'watching',
    icon: 'material-symbols:movie',
    image: '/images/subtitle/scenario-watching.jpeg',
    title: t('subtitlePage.scenarios.items.watching.title'),
    description: t('subtitlePage.scenarios.items.watching.description'),
    examples: [
      t('subtitlePage.scenarios.items.watching.examples.0'),
      t('subtitlePage.scenarios.items.watching.examples.1'),
      t('subtitlePage.scenarios.items.watching.examples.2')
    ]
  },
  {
    name: 'creating',
    icon: 'material-symbols:video-camera-front',
    image: '/images/subtitle/scenario-creating.jpeg',
    title: t('subtitlePage.scenarios.items.creating.title'),
    description: t('subtitlePage.scenarios.items.creating.description'),
    examples: [
      t('subtitlePage.scenarios.items.creating.examples.0'),
      t('subtitlePage.scenarios.items.creating.examples.1'),
      t('subtitlePage.scenarios.items.creating.examples.2')
    ]
  },
  {
    name: 'team',
    icon: 'material-symbols:group-work',
    image: '/images/subtitle/scenario-team.jpeg',
    title: t('subtitlePage.scenarios.items.team.title'),
    description: t('subtitlePage.scenarios.items.team.description'),
    examples: [
      t('subtitlePage.scenarios.items.team.examples.0'),
      t('subtitlePage.scenarios.items.team.examples.1'),
      t('subtitlePage.scenarios.items.team.examples.2')
    ]
  },
  {
    name: 'learning',
    icon: 'material-symbols:school',
    image: '/images/subtitle/scenario-learning.jpeg',
    title: t('subtitlePage.scenarios.items.learning.title'),
    description: t('subtitlePage.scenarios.items.learning.description'),
    examples: [
      t('subtitlePage.scenarios.items.learning.examples.0'),
      t('subtitlePage.scenarios.items.learning.examples.1'),
      t('subtitlePage.scenarios.items.learning.examples.2')
    ]
  }
]
</script>

<style scoped lang="scss">
.subtitle-scenarios {
  padding: $spacing-xxl 0;
  background: linear-gradient(135deg,
    rgba(#8B5CF6, 0.05) 0%,
    rgba(#4A90E2, 0.05) 100%);
}

.section-title {
  text-align: center;
  font-size: 2rem;
  font-weight: 700;
  color: $gray-900;
  margin-bottom: $spacing-xl;
}

.scenarios-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: $spacing-lg;
  max-width: 1200px;
  margin: 0 auto;
}

.scenario-card {
  background: $white;
  border-radius: $border-radius-lg;
  box-shadow: $shadow-md;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-8px);
    box-shadow: $shadow-lg;

    .scenario-image img {
      transform: scale(1.1);
    }

    .image-overlay {
      opacity: 1;
    }
  }
}

.scenario-image {
  position: relative;
  width: 100%;
  height: 180px;
  overflow: hidden;
  background: linear-gradient(135deg,
    rgba(#8B5CF6, 0.1) 0%,
    rgba(#4A90E2, 0.1) 100%);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }

  .image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg,
      rgba(#8B5CF6, 0.7) 0%,
      rgba(#4A90E2, 0.7) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;

    .iconify {
      color: $white;
      filter: drop-shadow(0 2px 4px rgba($black, 0.3));
    }
  }
}

.scenario-content {
  padding: $spacing-lg;
}

.scenario-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: $gray-900;
  margin: 0 0 $spacing-sm 0;
}

.scenario-description {
  font-size: 0.95rem;
  color: $gray-600;
  line-height: 1.6;
  margin: 0 0 $spacing-md 0;
}

.scenario-examples {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: $spacing-xs;

  li {
    display: flex;
    align-items: center;
    gap: $spacing-xs;
    font-size: 0.85rem;
    color: $gray-700;

    .iconify {
      color: #8B5CF6;
      flex-shrink: 0;
    }
  }
}

@media (max-width: 768px) {
  .scenarios-grid {
    grid-template-columns: 1fr;
    gap: $spacing-md;
  }

  .section-title {
    font-size: 1.75rem;
  }

  .scenario-image {
    height: 160px;
  }
}
</style>
